import { pipe } from 'fp-ts/function';
import { ioOption } from 'fp-ts';
import { useQueryClient } from 'react-query';

import { useRestMutation } from 'utils/hooks/api/useRestMutation';

import { Channel } from '../../domain';
import { UserMetadataQueryResponse } from '../../hooks/useUserMetadataQuery';
import { getMetadata, mergeMetadata, updateMetadata } from './utils';
import { ApiRequest } from '../../../../services/api';

const UNSUBSCRIBE_FROM_CHANNEL_API = '/users/profiles/unsubscribeUserFromChannel';

type UnsubscribeUserFromChannelMutationRequest = {
  channelIds: Channel['id'][];
  userKey: string;
};

const addToUnsubscribed =
  (updatedChannels: Channel['id'][]) =>
  (value: UserMetadataQueryResponse): Partial<UserMetadataQueryResponse> =>
    pipe(
      value.unsubscribedChannelIds,
      (previous) => [...previous, ...updatedChannels],
      (unsubscribedChannelIds) => ({ unsubscribedChannelIds }),
    );

export const useUnsubscribeUserFromChannelMutation = (defaultConfig?: ApiRequest) => {
  const queryClient = useQueryClient();
  return useRestMutation<unknown, UnsubscribeUserFromChannelMutationRequest>(
    UNSUBSCRIBE_FROM_CHANNEL_API,
    undefined,
    {
      onSuccess: (_data, { data }) => {
        const program = pipe(
          data.userKey,
          getMetadata(queryClient), // get Metadata saved in query cache
          ioOption.fromNullable,
          ioOption.map(mergeMetadata(addToUnsubscribed(data.channelIds))), // if we have data, mutate it by merging partial updates
          ioOption.tapIO(updateMetadata(data.userKey, queryClient)), // if we have data, mutate what is saved in query cache
        );

        program(); // Because we're using the IO type, we have to run the pipeline
      },
    },
    defaultConfig,
  );
};
